import { Settings } from 'luxon'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { defaultShouldDehydrateQuery, type Query } from '@tanstack/react-query'
import { offlineDb } from './db'
import { parseDateTimes } from '../utilities/helpers/parseDateTimes'

export const ECLIPSE_TZ_KEY = 'eclipse_tz'

const cachedTz = localStorage.getItem(ECLIPSE_TZ_KEY)
if (cachedTz) Settings.defaultZone = cachedTz

const dexieStorage = {
  getItem: async (key: string): Promise<string | null> => {
    const entry = await offlineDb.queryCache.get(key)
    return entry?.value ?? null
  },
  setItem: async (key: string, value: string): Promise<void> => {
    await offlineDb.queryCache.put({ key, value })
  },
  removeItem: async (key: string): Promise<void> => {
    await offlineDb.queryCache.delete(key)
  },
}

// Note: a refetch that fails, because the server is unreachable or erroring, leaves the query holding its last data and
// the page goes on showing it. The default keeps only queries whose last fetch succeeded, so a reload came back empty.
export const shouldPersistQuery = (query: Query) =>
  defaultShouldDehydrateQuery(query) || (query.state.status === 'error' && query.state.data !== undefined)

// Note: a failure from the last session says nothing about this one, so a query kept with its data comes back as that
// data, not as the error; it is still stale and refetches as any other would.
export function restoreQueryCache(cachedString: string) {
  const parsed = JSON.parse(cachedString)
  parsed?.clientState?.queries?.forEach((query: any) => {
    if (query?.state?.data != null) {
      parseDateTimes(query.state.data)
    }
    if (query?.state?.status === 'error' && query.state.data !== undefined) {
      Object.assign(query.state, { status: 'success', error: null, fetchFailureCount: 0, fetchFailureReason: null })
    }
  })
  return parsed
}

export const asyncPersister = createAsyncStoragePersister({
  storage: dexieStorage,
  key: 'ECLIPSE_QUERY_CACHE',
  throttleTime: 0,
  deserialize: restoreQueryCache,
})
